// src/components/ui/ImpersonationBanner.js
import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth.js';

const ImpersonationBanner = () => {
  const { currentUser, originalUser, stopImpersonation } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleStopImpersonation = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await stopImpersonation();
      // The auth context will update and this banner will unmount
    } catch (err) {
      console.error('Failed to stop impersonation:', err);
      setError('Failed to exit. Please try again.');
      setIsLoading(false);
    }
  };

  // Don't render if we don't have the required data
  if (!currentUser || !originalUser) {
    return null;
  }

  return (
    <div className="bg-amber-500 text-white px-4 py-2 sticky top-0 z-[100] shadow-md">
      <div className="container mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
        {/* Impersonation info */}
        <div className="flex items-center gap-2 text-sm sm:text-base">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-5 w-5 flex-shrink-0" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path 
              fillRule="evenodd" 
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
              clipRule="evenodd" 
            />
          </svg>
          <span>
            <span className="font-semibold">Viewing as:</span>{' '}
            {currentUser.firstName} {currentUser.lastName}{' '}
            <span className="hidden sm:inline text-amber-100">
              ({currentUser.email})
            </span>
          </span>
        </div>

        {/* Error message */}
        {error && (
          <span className="text-red-100 text-sm">{error}</span>
        )}

        {/* Exit button */}
        <button
          onClick={handleStopImpersonation}
          disabled={isLoading}
          className="bg-white text-amber-600 px-4 py-1 rounded font-medium text-sm
                     hover:bg-amber-50 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center gap-2"
        >
          {isLoading ? (
            <>
              <svg 
                className="animate-spin h-4 w-4" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                />
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Exiting...
            </>
          ) : (
            <>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-4 w-4" 
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path 
                  fillRule="evenodd" 
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
                  clipRule="evenodd" 
                />
              </svg>
              Exit Impersonation
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;